import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import IosShareIcon from '@mui/icons-material/IosShare';
import SearchIcon from '@mui/icons-material/Search';
import { PG, pgBtn } from './theme';
import { byUpdatedDesc, formatWhen } from './helpers';
import { nutritionRouteKey, type NutritionRoute } from './nutritionRoutes';

export type ExportPlatform = 'easyai' | 'nutrition' | 'sdk';

export type ContextExportInitial = {
  platform: ExportPlatform;
  title?: string;
  chainIds: string[];
  functionIds: string[];
  objectIds: string[];
  /** NutritionAI route keys (nutrition) or SDK capability keys (sdk). */
  routeIds: string[];
  docUrls: string[];
  sdkPlatforms: string[];
};

type CatalogHit = {
  type: 'chain' | 'function' | 'object' | 'category';
  id: string;
  name: string;
  description?: string | null;
  updated_at?: string | null;
};

type DocPage = { title: string; url: string; hint?: string };

type ExportResult = { filename: string; markdown: string; chars: number; missing: string[] };

type Props = {
  open: boolean;
  onClose: () => void;
  authHeaders: () => Promise<HeadersInit>;
  initial: ContextExportInitial;
};

const PLATFORMS: { id: ExportPlatform; label: string; blurb: string }[] = [
  { id: 'easyai', label: 'EasyAI', blurb: 'Chains, AI functions and objects called over the EasyAI REST API.' },
  { id: 'nutrition', label: 'EasyAI + NutritionAI', blurb: 'Adds NutritionAI REST routes, their contracts and the REST docs.' },
  { id: 'sdk', label: 'Mobile SDK', blurb: 'Adds SDK capabilities (with the SDK method for each) and the mobile SDK docs.' },
];

const SDK_PLATFORM_OPTIONS = [
  { id: 'ios', label: 'iOS (Swift)' },
  { id: 'android', label: 'Android (Kotlin)' },
  { id: 'react_native', label: 'React Native' },
  { id: 'flutter', label: 'Flutter (Dart)' },
];

const toggleIn = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

type PickRow = { id: string; primary: string; secondary?: string };

const PickList: React.FC<{
  rows: PickRow[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  loading?: boolean;
  error?: string | null;
  empty: string;
  limit?: number;
}> = ({ rows, selected, onChange, placeholder, loading, error, empty, limit }) => {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? rows.filter((r) => `${r.primary} ${r.secondary || ''}`.toLowerCase().includes(needle)) : rows;
  }, [rows, q]);
  const atLimit = limit !== undefined && selected.length >= limit;
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          fullWidth
          placeholder={placeholder}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {selected.length > 0 && (
          <Button size="small" onClick={() => onChange([])} sx={{ ...pgBtn, color: PG.muted, flexShrink: 0 }}>
            Clear ({selected.length})
          </Button>
        )}
      </Stack>
      {error && <Alert severity="warning">{error}</Alert>}
      <Box sx={{ border: `1px solid ${PG.border}`, borderRadius: 1.5, maxHeight: 320, overflowY: 'auto' }}>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={22} />
          </Stack>
        ) : shown.length === 0 ? (
          <Typography variant="body2" sx={{ color: PG.muted, p: 2 }}>
            {rows.length === 0 ? empty : 'Nothing matches.'}
          </Typography>
        ) : (
          <List dense disablePadding>
            {shown.map((r) => {
              const on = selected.includes(r.id);
              return (
                <ListItemButton
                  key={r.id}
                  onClick={() => (on || !atLimit) && onChange(toggleIn(selected, r.id))}
                  disabled={!on && atLimit}
                  sx={{ py: 0.5, alignItems: 'flex-start' }}
                >
                  <Checkbox size="small" checked={on} tabIndex={-1} disableRipple sx={{ p: 0.5, mr: 1 }} />
                  <ListItemText
                    primary={r.primary}
                    secondary={r.secondary}
                    primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                    secondaryTypographyProps={{ fontSize: 12, color: PG.muted, sx: { overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' } }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
      {atLimit && (
        <Typography variant="caption" sx={{ color: PG.muted }}>
          Limit reached ({limit}). Split very large exports into several files.
        </Typography>
      )}
    </Stack>
  );
};

export const ContextExportWizard: React.FC<Props> = ({ open, onClose, authHeaders, initial }) => {
  const [step, setStep] = useState(0);
  const [platform, setPlatform] = useState<ExportPlatform>(initial.platform);
  const [title, setTitle] = useState('');
  const [purpose, setPurpose] = useState('');
  const [basics, setBasics] = useState(true);
  const [platformPack, setPlatformPack] = useState(true);
  const [studioPack, setStudioPack] = useState(false);
  const [sdkPlatforms, setSdkPlatforms] = useState<string[]>([]);
  const [chainIds, setChainIds] = useState<string[]>([]);
  const [functionIds, setFunctionIds] = useState<string[]>([]);
  const [objectIds, setObjectIds] = useState<string[]>([]);
  const [routeIds, setRouteIds] = useState<string[]>([]);
  const [docUrls, setDocUrls] = useState<string[]>([]);
  const [easyTab, setEasyTab] = useState<'chain' | 'function' | 'object'>('chain');

  const [catalog, setCatalog] = useState<CatalogHit[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [routes, setRoutes] = useState<Record<string, NutritionRoute[]>>({});
  const [routesLoading, setRoutesLoading] = useState(false);
  const [routesError, setRoutesError] = useState<string | null>(null);
  const [docs, setDocs] = useState<Record<string, DocPage[]>>({});
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [result, setResult] = useState<ExportResult | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setPlatform(initial.platform);
    setTitle(initial.title || '');
    setPurpose('');
    setBasics(true);
    setPlatformPack(true);
    setStudioPack(false);
    setSdkPlatforms(initial.sdkPlatforms);
    setChainIds(initial.chainIds);
    setFunctionIds(initial.functionIds);
    setObjectIds(initial.objectIds);
    setRouteIds(initial.routeIds);
    setDocUrls(initial.docUrls);
    setResult(null);
    setExportError(null);
    // Seed once per open; later edits to the session should not wipe picks mid-wizard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      setCatalogError(null);
      try {
        const res = await fetch('/api/v2/playground/catalog?types=chain,function,object', { headers: await authHeaders() });
        if (!res.ok) throw new Error('Could not load chains and functions');
        const rows: CatalogHit[] = await res.json();
        if (!cancelled) setCatalog(rows);
      } catch (e: any) {
        if (!cancelled) setCatalogError(e.message || 'Could not load chains and functions');
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, authHeaders]);

  useEffect(() => {
    if (!open || platform === 'easyai' || routes[platform]) return;
    let cancelled = false;
    (async () => {
      setRoutesLoading(true);
      setRoutesError(null);
      try {
        const url = platform === 'sdk' ? '/api/v2/playground/sdk/capabilities' : '/api/v2/playground/nutrition/openapi';
        const res = await fetch(url, { headers: await authHeaders() });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.detail || 'Could not load the route catalog');
        if (!cancelled) setRoutes((prev) => ({ ...prev, [platform]: Array.isArray(body.routes) ? body.routes : [] }));
      } catch (e: any) {
        if (!cancelled) setRoutesError(e.message || 'Could not load the route catalog');
      } finally {
        if (!cancelled) setRoutesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, platform, routes, authHeaders]);

  useEffect(() => {
    if (!open || platform === 'easyai' || docs[platform]) return;
    let cancelled = false;
    (async () => {
      setDocsLoading(true);
      setDocsError(null);
      try {
        const url = platform === 'sdk' ? '/api/v2/playground/sdk/docs' : '/api/v2/playground/nutrition/docs';
        const res = await fetch(url, { headers: await authHeaders() });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.detail || 'Could not load the docs index');
        if (!cancelled) setDocs((prev) => ({ ...prev, [platform]: Array.isArray(body.pages) ? body.pages : [] }));
      } catch (e: any) {
        if (!cancelled) setDocsError(e.message || 'Could not load the docs index');
      } finally {
        if (!cancelled) setDocsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, platform, docs, authHeaders]);

  const choosePlatform = (next: ExportPlatform) => {
    if (next === platform) return;
    setPlatform(next);
    // Route keys and doc URLs are platform-specific; carrying them across would export the wrong half.
    setRouteIds(next === initial.platform ? initial.routeIds : []);
    setDocUrls(next === initial.platform ? initial.docUrls : []);
    setResult(null);
  };

  const steps = useMemo(() => {
    const out: { key: string; label: string }[] = [
      { key: 'scope', label: 'Scope' },
      { key: 'easyai', label: 'EasyAI' },
    ];
    if (platform !== 'easyai') {
      out.push({ key: 'routes', label: platform === 'sdk' ? 'Capabilities' : 'Routes' });
      out.push({ key: 'docs', label: 'Docs' });
    }
    out.push({ key: 'export', label: 'Export' });
    return out;
  }, [platform]);
  const current = steps[Math.min(step, steps.length - 1)].key;
  const isLast = step >= steps.length - 1;

  const catalogRows = (type: CatalogHit['type']): PickRow[] =>
    catalog
      .filter((h) => h.type === type)
      .sort(byUpdatedDesc)
      .map((h) => ({
        id: h.id,
        primary: h.name,
        secondary: [formatWhen(h.updated_at), h.description].filter(Boolean).join(' · '),
      }));
  const routeRows: PickRow[] = (routes[platform] || []).map((r) => ({
    id: nutritionRouteKey(r),
    primary: platform === 'sdk' && r.sdk_method ? `${r.sdk_method}  ·  ${r.summary || r.path}` : `${r.method} ${r.path}`,
    secondary: platform === 'sdk' ? `${r.method} ${r.path}${r.sdk_method ? '' : ' · REST only'}` : r.summary,
  }));
  const docRows: PickRow[] = (docs[platform] || []).map((p) => ({ id: p.url, primary: p.title, secondary: p.hint || p.url }));

  const nameFor = (type: CatalogHit['type'], id: string) => catalog.find((h) => h.type === type && h.id === id)?.name || id.slice(0, 8);
  const routeLabel = (id: string) => {
    const r = (routes[platform] || []).find((x) => nutritionRouteKey(x) === id);
    return r ? (platform === 'sdk' && r.sdk_method ? r.sdk_method : `${r.method} ${r.path}`) : id;
  };
  const docLabel = (url: string) => (docs[platform] || []).find((p) => p.url === url)?.title || url.split('/').pop() || url;

  const platformPackLabel =
    platform === 'sdk' ? 'Mobile SDK packs (core + picked platforms) and the capability index' : platform === 'nutrition' ? 'NutritionAI packs (auth, feedback, response shapes) and the route index' : '';

  const nothingPicked =
    !basics && !(platformPack && platform !== 'easyai') && !studioPack && !chainIds.length && !functionIds.length && !objectIds.length && !routeIds.length && !docUrls.length;

  const download = (r: ExportResult) => {
    const blob = new Blob([r.markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = r.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runExport = async () => {
    setExporting(true);
    setExportError(null);
    setResult(null);
    try {
      const res = await fetch('/api/v2/playground/context-export', {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          platform,
          title: title.trim() || null,
          purpose: purpose.trim() || null,
          include_easyai_basics: basics,
          include_platform_pack: platform !== 'easyai' && platformPack,
          include_studio_pack: studioPack,
          chain_ids: chainIds,
          function_ids: functionIds,
          object_ids: objectIds,
          route_ids: platform === 'easyai' ? [] : routeIds,
          doc_urls: platform === 'easyai' ? [] : docUrls,
          sdk_platforms: platform === 'sdk' ? sdkPlatforms : [],
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === 'string' ? body.detail : 'Could not build the export');
      setResult(body as ExportResult);
      download(body as ExportResult);
    } catch (e: any) {
      setExportError(e.message || 'Could not build the export');
    } finally {
      setExporting(false);
    }
  };

  const copy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setExportError('Could not copy to the clipboard. Use Download instead.');
    }
  };

  const summaryRow = (label: string, items: string[]) =>
    items.length ? (
      <Box key={label}>
        <Typography variant="caption" sx={{ color: PG.muted, fontWeight: 700 }}>
          {label} ({items.length})
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
          {items.map((t, i) => (
            <Chip key={`${t}-${i}`} size="small" label={t} sx={{ bgcolor: 'rgba(56,189,248,0.14)', color: PG.text, maxWidth: 260 }} />
          ))}
        </Stack>
      </Box>
    ) : null;

  const renderStep = () => {
    if (current === 'scope') {
      return (
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: PG.muted, lineHeight: 1.5 }}>
            Pick what a developer needs, and download it as one Markdown file to add to their own project and reference in Cursor. Chains include their inputs, steps, result node, result schema and an example result.
          </Typography>
          <Box>
            <Typography variant="caption" sx={{ color: PG.muted, fontWeight: 700 }}>
              Platform
            </Typography>
            <ToggleButtonGroup exclusive size="small" value={platform} onChange={(_, v) => v && choosePlatform(v)} sx={{ display: 'flex', mt: 0.5 }}>
              {PLATFORMS.map((p) => (
                <ToggleButton key={p.id} value={p.id} sx={{ flex: 1, textTransform: 'none', fontWeight: 700, color: PG.muted, '&.Mui-selected': { color: PG.text, bgcolor: 'rgba(56,189,248,0.18)' } }}>
                  {p.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography variant="caption" sx={{ color: PG.muted, display: 'block', mt: 0.5 }}>
              {PLATFORMS.find((p) => p.id === platform)?.blurb}
              {platform === initial.platform ? ' Matches this chat.' : ''}
            </Typography>
          </Box>
          {platform === 'sdk' && (
            <Box>
              <Typography variant="caption" sx={{ color: PG.muted, fontWeight: 700 }}>
                SDK platforms
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
                {SDK_PLATFORM_OPTIONS.map((p) => {
                  const on = sdkPlatforms.includes(p.id);
                  return (
                    <Chip
                      key={p.id}
                      label={p.label}
                      onClick={() => setSdkPlatforms((prev) => toggleIn(prev, p.id))}
                      variant={on ? 'filled' : 'outlined'}
                      sx={{ color: PG.text, borderColor: PG.border, bgcolor: on ? 'rgba(167,139,250,0.28)' : 'transparent', fontWeight: 600 }}
                    />
                  );
                })}
              </Stack>
            </Box>
          )}
          <TextField size="small" label="Title" placeholder="e.g. Weekly meal plan integration" value={title} onChange={(e) => setTitle(e.target.value)} inputProps={{ maxLength: 200 }} />
          <TextField
            size="small"
            label="What are you building? (optional)"
            placeholder="A short brief for Cursor, e.g. Add a meal-plan page to our Next.js app that runs the weekly plan chain and renders the days."
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            multiline
            minRows={2}
            inputProps={{ maxLength: 4000 }}
          />
          <Stack>
            <FormControlLabel control={<Checkbox size="small" checked={basics} onChange={(e) => setBasics(e.target.checked)} />} label={<Typography variant="body2">EasyAI API essentials: auth, functions, chain execute and poll, uploads, feedback, reference client</Typography>} />
            {platform !== 'easyai' && (
              <FormControlLabel control={<Checkbox size="small" checked={platformPack} onChange={(e) => setPlatformPack(e.target.checked)} />} label={<Typography variant="body2">{platformPackLabel}</Typography>} />
            )}
            <FormControlLabel control={<Checkbox size="small" checked={studioPack} onChange={(e) => setStudioPack(e.target.checked)} />} label={<Typography variant="body2">Full EasyAI App Builder knowledge pack (long; written for Studio)</Typography>} />
          </Stack>
        </Stack>
      );
    }
    if (current === 'easyai') {
      const counts = { chain: chainIds.length, function: functionIds.length, object: objectIds.length };
      const tabLabel = { chain: 'Chains', function: 'Functions', object: 'Objects' } as const;
      return (
        <Stack spacing={1.5}>
          <ToggleButtonGroup exclusive size="small" value={easyTab} onChange={(_, v) => v && setEasyTab(v)}>
            {(['chain', 'function', 'object'] as const).map((t) => (
              <ToggleButton key={t} value={t} sx={{ textTransform: 'none', fontWeight: 700, color: PG.muted, px: 2, '&.Mui-selected': { color: PG.text, bgcolor: 'rgba(56,189,248,0.18)' } }}>
                {tabLabel[t]}
                {counts[t] ? ` (${counts[t]})` : ''}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {easyTab === 'chain' && (
            <PickList rows={catalogRows('chain')} selected={chainIds} onChange={setChainIds} placeholder="Search chains…" loading={catalogLoading} error={catalogError} empty="No chains on this account yet." limit={40} />
          )}
          {easyTab === 'function' && (
            <PickList rows={catalogRows('function')} selected={functionIds} onChange={setFunctionIds} placeholder="Search AI functions…" loading={catalogLoading} error={catalogError} empty="No AI functions on this account yet." limit={40} />
          )}
          {easyTab === 'object' && (
            <PickList rows={catalogRows('object')} selected={objectIds} onChange={setObjectIds} placeholder="Search custom objects…" loading={catalogLoading} error={catalogError} empty="No custom objects on this account yet." limit={40} />
          )}
          <Typography variant="caption" sx={{ color: PG.muted }}>
            Items attached to this chat are already ticked. Newest first.
          </Typography>
        </Stack>
      );
    }
    if (current === 'routes') {
      return (
        <Stack spacing={1}>
          <Typography variant="body2" sx={{ color: PG.muted }}>
            {platform === 'sdk'
              ? 'Each pick exports the SDK method to call and the full request/response contract it wraps.'
              : 'Each pick exports the full request and response contract from the live NutritionAI OpenAPI spec.'}
          </Typography>
          <PickList
            rows={routeRows}
            selected={routeIds}
            onChange={setRouteIds}
            placeholder={platform === 'sdk' ? 'Search by SDK method, path or summary…' : 'Search by path, summary or tag…'}
            loading={routesLoading}
            error={routesError}
            empty="No routes available."
            limit={80}
          />
        </Stack>
      );
    }
    if (current === 'docs') {
      return (
        <Stack spacing={1}>
          <Typography variant="body2" sx={{ color: PG.muted }}>
            Official Passio docs pages, fetched in full as Markdown when you export ({platform === 'sdk' ? 'mobile SDK' : 'REST API'} section).
          </Typography>
          <PickList rows={docRows} selected={docUrls} onChange={setDocUrls} placeholder="Search docs pages…" loading={docsLoading} error={docsError} empty="No docs pages available." limit={25} />
        </Stack>
      );
    }
    return (
      <Stack spacing={1.5}>
        <Typography variant="body2" sx={{ color: PG.muted }}>
          {PLATFORMS.find((p) => p.id === platform)?.label}
          {platform === 'sdk' && sdkPlatforms.length ? ` · ${sdkPlatforms.map((id) => SDK_PLATFORM_OPTIONS.find((p) => p.id === id)?.label || id).join(', ')}` : ''}
          {title.trim() ? ` · “${title.trim()}”` : ''}
        </Typography>
        {summaryRow('Included packs', [
          ...(basics ? ['EasyAI API essentials'] : []),
          ...(platform !== 'easyai' && platformPack ? [platform === 'sdk' ? 'Mobile SDK packs' : 'NutritionAI packs'] : []),
          ...(studioPack ? ['App Builder knowledge pack'] : []),
        ])}
        {summaryRow('Chains', chainIds.map((id) => nameFor('chain', id)))}
        {summaryRow('Functions', functionIds.map((id) => nameFor('function', id)))}
        {summaryRow('Objects', objectIds.map((id) => nameFor('object', id)))}
        {platform !== 'easyai' && summaryRow(platform === 'sdk' ? 'Capabilities' : 'Routes', routeIds.map(routeLabel))}
        {platform !== 'easyai' && summaryRow('Docs pages', docUrls.map(docLabel))}
        {nothingPicked && <Alert severity="info">Nothing picked yet. Go back and choose at least one item or pack.</Alert>}
        {exportError && <Alert severity="error">{exportError}</Alert>}
        {result && (
          <Alert
            severity="success"
            action={
              <Stack direction="row" spacing={0.5}>
                <Button size="small" startIcon={<DownloadIcon />} onClick={() => download(result)} sx={pgBtn}>
                  Download
                </Button>
                <Button size="small" startIcon={<ContentCopyIcon />} onClick={copy} sx={pgBtn}>
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </Stack>
            }
          >
            Downloaded <strong>{result.filename}</strong> ({Math.max(1, Math.round(result.chars / 1024))} KB). Add it to your project and reference it in Cursor with @{result.filename}.
            {result.missing.length > 0 && ` Skipped (no longer available): ${result.missing.join(', ')}.`}
          </Alert>
        )}
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" PaperProps={{ sx: { bgcolor: PG.panel, color: PG.text } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1, pr: 6 }}>
        <IosShareIcon fontSize="small" sx={{ color: PG.accent }} />
        Export context for Cursor
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8, color: PG.muted }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: PG.border }}>
        <Stepper
          activeStep={step}
          alternativeLabel
          sx={{
            mb: 2.5,
            '& .MuiStepLabel-label': { fontSize: 11, color: PG.muted },
            '& .MuiStepLabel-label.Mui-active': { color: PG.text, fontWeight: 700 },
            '& .MuiStepLabel-label.Mui-completed': { color: PG.muted },
          }}
        >
          {steps.map((s) => (
            <Step key={s.key}>
              <StepLabel>{s.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {renderStep()}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Typography variant="caption" sx={{ color: PG.muted, mr: 'auto', pl: 1 }}>
          {chainIds.length + functionIds.length + objectIds.length + (platform === 'easyai' ? 0 : routeIds.length + docUrls.length)} items picked
        </Typography>
        {step > 0 && (
          <Button onClick={() => setStep((s) => Math.max(0, s - 1))} sx={pgBtn} disabled={exporting}>
            Back
          </Button>
        )}
        {isLast ? (
          <Button variant="contained" startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon />} onClick={runExport} disabled={exporting || nothingPicked} sx={pgBtn}>
            {exporting ? 'Building…' : result ? 'Export again' : 'Export Markdown'}
          </Button>
        ) : (
          <Button variant="contained" onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))} sx={pgBtn}>
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContextExportWizard;
